import { DestroyRef } from '@angular/core';

import { Point } from '@models/shared/geometry';
import { Delta } from '@models/gridlayout/delta';
import { catchError, debounceTime, from, map, Observable, of } from 'rxjs';

export function getScrollBarSize(): [number, number] {
    const inner = document.createElement('p');
    inner.style.width = '100%';
    inner.style.height = '100%';

    const outer = document.createElement('div');
    outer.style.position = 'absolute';
    outer.style.top = '0px';
    outer.style.left = '0px';
    outer.style.visibility = 'hidden';
    outer.style.width = '100px';
    outer.style.height = '100px';
    outer.style.overflow = 'hidden';
    outer.appendChild(inner);

    document.body.appendChild(outer);

    const w1 = inner.offsetWidth;
    const h1 = inner.offsetHeight;
    outer.style.overflow = 'scroll';
    let w2 = inner.offsetWidth;
    let h2 = inner.offsetHeight;
    if (w1 == w2) {
        w2 = outer.clientWidth;
    }
    if (h1 == h2) {
        h2 = outer.clientHeight;
    }

    document.body.removeChild(outer);

    return [(w1 - w2), (h1 - h2)];
}

export function isMobile(): boolean {
    const multiTouch = 'maxTouchPoints' in navigator && navigator.maxTouchPoints > 0;
    const mQ = matchMedia?.('(pointer:coarse)');
    const pc = mQ?.media === '(pointer:coarse)' && mQ.matches;
    const orientation = 'orientation' in window;
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
    const uaMob = /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(ua) || /\b(Android|Windows Phone|iPad|iPod)\b/i.test(ua);
    const wh = 'screen' in window && (
        (window.screen.availWidth < 1300 && window.screen.availHeight < 700)
        || (window.screen.availWidth < 700 && window.screen.availHeight < 1300)
    );
    return uaMob || (wh && (multiTouch || pc || orientation));
}

export function getDpi(): number {
    const div = document.createElement('div');
    div.style.width = '1in';
    div.style.height = '1in';
    div.style.position = 'absolute';
    div.style.top = '-100%';
    div.style.left = '-100%';
    document.body.appendChild(div);
    const dpr = window.devicePixelRatio || 1;
    const dpi = Math.max(div.offsetWidth * dpr, div.offsetHeight * dpr);
    document.body.removeChild(div);
    return dpi;
}

export function stopEvent(e: any) {
    if (e) {
        if (e.stopImmediatePropagation) {
            e.stopImmediatePropagation();
        }
        if (e.preventDefault) {
            e.preventDefault();
        }
    }
}

export function scrollIntoViewIfNeeded(target?: Element | null) {
    if (!target) {
        return;
    }
    const bottom = target.parentElement ? target.parentElement.getBoundingClientRect().bottom : window.innerHeight;
    const top = target.parentElement ? target.parentElement.getBoundingClientRect().top : 0;
    if (target.getBoundingClientRect().bottom > bottom) {
        target.scrollIntoView(false);
    }
    if (target.getBoundingClientRect().top < top) {
        target.scrollIntoView();
    }
}

export function onElementRemove(element: any, onDetachCallback: () => void): void {
    const observer = new MutationObserver(function () {
        function isDetached(el: any): boolean {
            if (el.parentNode === document) {
                return false;
            }
            else if (el.parentNode === null) {
                return true;
            }
            else {
                return isDetached(el.parentNode);
            }
        }
        if (isDetached(element)) {
            observer.disconnect();
            onDetachCallback();
        }
    })
    observer.observe(document, { childList: true, subtree: true });
}

export function isSelectionOnlyIn(element?: HTMLElement | null): boolean {
    const sel = window.getSelection();
    if (!element || !sel || sel.rangeCount < 1) {
        return false;
    }
    const range = sel.getRangeAt(0);
    if (range.collapsed) {
        return false;
    }
    const cont = range.commonAncestorContainer;
    return element.contains(cont);
}

export function getEventDelta(e: MouseEvent, p: Point = { x: 0, y: 0 }): Delta {
    // tslint:disable-next-line: deprecation
    e = e || window.event as MouseEvent;
    let x = 0;
    if (e.pageX) {
        x = e.pageX;
    }
    else if (e.clientX) {
        x = e.clientX;
    }
    let y = 0;
    if (e.pageY) {
        y = e.pageY;
    }
    else if (e.clientY) {
        y = e.clientY;
    }
    return { dx: x - p.x, dy: y - p.y };
}

export function isSupportsPDFs(): boolean {
    const nav = window.navigator;
    const ua = window.navigator.userAgent;
    const isModernBrowser = (window.Promise !== undefined);
    const supportsPdfMimeType = (nav.mimeTypes && (nav.mimeTypes as any)['application/pdf'] !== undefined);
    const isSafariIOSDesktopMode = (nav.platform !== undefined && nav.platform === 'MacIntel' && nav.maxTouchPoints !== undefined && nav.maxTouchPoints > 1);
    const isMobileDevice = (isSafariIOSDesktopMode || isMobile());
    const isFirefoxWithPDFJS = (!isMobileDevice && /irefox/.test(ua) && ua.split('rv:').length > 1) ? (parseInt(ua.split('rv:')[1].split('.')[0], 10) > 18) : false;
    return !isMobileDevice && (isModernBrowser || isFirefoxWithPDFJS || supportsPdfMimeType || nav.pdfViewerEnabled);
}

export function copyHtmlToClipboard(html: string, text: string): Observable<boolean> {
    try {
        const ci = new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' })
        });
        return from(navigator.clipboard.write([ci])).pipe(map(() => true), catchError(() => of(false)));
    }
    catch {
        return of(false);
    }
}

export function observeResize(destRef: DestroyRef | null, el: Element | undefined, debounce = 0): Observable<Element | undefined> {
    const res = new Observable<Element | undefined>(obs => {
        obs.next(el);
        if (ResizeObserver && el) {
            const ro = new ResizeObserver(entries => {
                if (entries[0]) {
                    obs.next(el);
                }
            });
            try {
                ro.observe(el);
                destRef?.onDestroy(() => {
                    ro.disconnect();
                    obs.complete();
                });
            }
            catch {
                ro?.disconnect();
                obs.complete();
            }
        }
        else {
            obs.complete();
        }
    });
    return debounce > 0 ? res.pipe(debounceTime(debounce)) : res;
}

export function crEl<T extends keyof HTMLElementTagNameMap>(tagName: T, classes?: string, styles?: { [name: string]: string }): HTMLElementTagNameMap[T] {
    const el = document.createElement(tagName);
    if (classes) {
        el.className = classes;
    }
    if (styles) {
        Object.keys(styles).forEach(k => (el as any).style[k] = styles[k]);
    }
    return el;
}

export function focusEl(id?: string, opt: { max?: number, cur?: number, select?: boolean } = {}): void {
    opt.max ||= 500;
    opt.cur ||= 50;
    if (!id || opt.cur > opt.max) {
        return;
    }
    const el: any = document.getElementById(id);
    if (el && el != document.activeElement) {
        el.focus?.();
        if (opt.select) {
            el.select?.();
        }
    }
    opt.cur *= 2;
    setTimeout(() => focusEl(id, opt));
}

export function isElemVisible(elem: HTMLElement): boolean {
    if (!(!!elem && !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length))) {
        return false;
    }
    if (elem == document.body) {
        return true;
    }
    const { top, left, height, width } = elem.getBoundingClientRect();
    let el: any = elem.parentNode;
    do {
        const rect = el.getBoundingClientRect();
        if (rect.height > 0 && rect.width > 0) {
            if (
                (top <= rect.bottom === false)
                || ((top + height) <= rect.top)
                || (left <= rect.right === false)
                || ((left + width) <= rect.left)
            ) {
                return false;
            }
        }
        el = el.parentNode;
    } while (el != document.body);
    return top <= document.documentElement.clientHeight && left <= document.documentElement.clientWidth;
}

export type PositionObserverData = {
    x: number;
    y: number;
    target: HTMLElement | Element;
    outOfViewport: boolean;
    rootBounds: DOMRect | null;
};
export type PositionObserverCallback = (data: PositionObserverData) => unknown;
export class PositionObserver {
    private intersectionObserver!: IntersectionObserver;
    private positionObserverCallback;
    private thresholdList;

    constructor(
        positionObserverCallback: PositionObserverCallback,
        thresholdFraction = 1000
    ) {
        this.positionObserverCallback = positionObserverCallback;
        this.thresholdList = new Array(thresholdFraction + 1)
            .fill(0)
            .map((_, ind) => ind / thresholdFraction);
    }

    public observe(targetElement: HTMLElement | Element): void {
        this.intersectionObserver = new IntersectionObserver(
            this.viewportCallback.bind(this),
            {
                threshold: this.thresholdList,
                rootMargin: "0px 0px 0px 0px",
                root: document
            }
        );
        this.intersectionObserver.observe(targetElement);
    }

    private viewportCallback(e: IntersectionObserverEntry[]): void {
        const entry = e[0];
        const target = entry.target;
        const targetBounds = entry.boundingClientRect;
        if (this.positionObserverCallback) {
            this.positionObserverCallback({
                x: Math.trunc(targetBounds.left),
                y:  Math.trunc(targetBounds.top),
                target,
                outOfViewport: !entry.isIntersecting,
                rootBounds: entry.rootBounds,
            });
        }
        if (entry.intersectionRatio > 0) {
            this.intersectionObserver?.unobserve(target);
            this.intersectionObserver?.disconnect();
            const options: IntersectionObserverInit = {
                threshold: this.thresholdList,
                rootMargin: this.getMargins(
                    this.constructBoxWindow(targetBounds, entry.rootBounds as DOMRect),
                    entry.rootBounds
                ),
                root: document
            };
            this.intersectionObserver = new IntersectionObserver(
                this.intersectionObsCallback.bind(this),
                options
            );
            this.intersectionObserver?.observe(target);
        }
    }

    private intersectionObsCallback(e: IntersectionObserverEntry[]): void {
        const entry = e[0];
        const target = entry.target;
        const targetBounds = entry.boundingClientRect;
        if (this.positionObserverCallback) {
            this.positionObserverCallback({
                x: Math.trunc(targetBounds.left),
                y: Math.trunc(targetBounds.top),
                target,
                outOfViewport: false,
                rootBounds: entry.rootBounds,
            });
        }
        if (entry.intersectionRatio === 0) {
            this.intersectionObserver?.unobserve(target);
            this.intersectionObserver?.disconnect();
            this.intersectionObserver = new IntersectionObserver(
                this.viewportCallback.bind(this),
                {
                    threshold: this.thresholdList,
                    rootMargin: "0px 0px 0px 0px",
                    root: document
                }
            );
            this.intersectionObserver?.observe(target);
        }
    }

    public disconnect(): void {
        this.intersectionObserver?.disconnect();
    }

    private constructBoxWindow(targetBounds: DOMRect, rootBounds: DOMRect): {
        left: number;
        top: number;
        right: number;
        bottom: number;
    } {
        const constrainedLeft = Math.min(
            Math.max(rootBounds.left, targetBounds.left),
            rootBounds.right - targetBounds.width
        );
        const constrainedTop = Math.min(
            Math.max(rootBounds.top, targetBounds.top),
            rootBounds.bottom - targetBounds.height
        );
        const constrainedRight = constrainedLeft + targetBounds.width;
        const constrainedBottom = constrainedTop + targetBounds.height;
        return {
            left: constrainedLeft,
            top: constrainedTop,
            right: constrainedRight,
            bottom: constrainedBottom,
        };
    }

    private getMargins(
        windowDimensions: {
            left: number;
            top: number;
            right: number;
            bottom: number;
        },
        rootBounds: DOMRect | null
    ): string {
        let viewportBounds;
        if (!rootBounds) {
            viewportBounds = {
                top: visualViewport!.offsetTop,
                left: visualViewport!.offsetLeft,
                right: visualViewport!.width + visualViewport!.offsetLeft,
                bottom: visualViewport!.height + visualViewport!.offsetTop,
            };
        } else {
            viewportBounds = rootBounds;
        }
        const constrainedTopMarginOfWindow = Math.min(
            -(windowDimensions.top - 1 - viewportBounds.top),
            0
        );
        const constrainedRightMarginOfWindow = Math.min(
            -(viewportBounds.right - (windowDimensions.right + 1)),
            0
        );
        const constrainedBottomMarginOfWindow = Math.min(
            -(viewportBounds.bottom - (windowDimensions.bottom + 1)),
            0
        );
        const constrainedLeftMarginOfWindow = Math.min(
            -(windowDimensions.left - 1 - viewportBounds.left),
            0
        );
        return `${constrainedTopMarginOfWindow}px ${constrainedRightMarginOfWindow}px ${constrainedBottomMarginOfWindow}px ${constrainedLeftMarginOfWindow}px`;
    }

}